import { useState } from 'react';

export const usePricingApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  /**
   * Transform form data into pricing API parameters
   * @param {Object} formData - The cruise booking form data
   * @returns {Object} - Formatted parameters for pricing API
   */
  const transformFormDataToPricingParams = (formData) => {
    const params = {
      origin: {},
      destination: {},
      direction: {
        id: 'direction',
        type: 'direction'
      },
      brand_id: window.gon?.current_brand || 'shipplay',
      sessiontoken: formData.sessionToken || generateSessionToken(),
      partner_id: window.gon?.partner_id || '',
      
      // Cruise-specific fields (keeping existing cruise logic)
      cruise: 'true',
      trip_type: formData.tripType?.id || '',
      cruise_line: formData.currentCruiseLine?.name || '',
      cruise_port: formData.currentPort?.name || '',
      cruise_port_id: formData.currentPort?.port_id || '',
      cruise_session_id: formData.currentItinerary?.id || '',
      embarkation_date: formData.currentItinerary?.embarkation_date || '',
      disembarkation_date: formData.currentItinerary?.disembarkation_date || ''
    };

    // Set origin and destination based on trip type
    if (formData.tripType?.id !== 'from-cruise') {
      // To cruise - origin is user address, destination is cruise port
      params.origin = {
        id: formData.fromAddress?.place_id || formData.fromAddress?._id || 'placeholder',
        type: 'GooglePlace'
      };

      if (formData.toAddress) {
        params.destination = {
          id: formData.toAddress?.google_place_id || formData.toAddress?.place_id || 'placeholder',
          type: 'GooglePlace'
        };
      }
    } else {
      // From cruise - origin is cruise port, destination is user address
      if (formData.fromAddress) {
        params.origin = {
          id: formData.fromAddress?.google_place_id || formData.fromAddress?.place_id || 'placeholder',
          type: 'GooglePlace'
        };
      }

      params.destination = {
        id: formData.toAddress?.place_id || formData.toAddress?._id || 'placeholder',
        type: 'GooglePlace'
      };
    }

    // Add cruise ship or tour name
    const selectField = formData.itineraryFilterField === 'tour_name' ? 'cruise_tour_name' : 'cruise_ship';
    params[selectField] = formData.currentShip || '';

    return params;
  };

  /**
   * Generate a random session token if not provided
   * @returns {string} - Random session token
   */
  const generateSessionToken = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < 19; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  /**
   * Transform pricing response into payload format
   * @param {Object} response - API response from pricing endpoint
   * @returns {Object} - Transformed payload for further processing
   */
  const transformResponseToPayload = (response) => {
    if (!response) return null;

    return {
      brand_id: window.gon?.current_brand || '',
      rates: response.rates || [],
      origin: {
        country_code: response.addresses?.origin?.o_country || '',
        country_name: response.addresses?.origin?.o_country_name || '',
        state: response.addresses?.origin?.o_state || '',
        city: response.addresses?.origin?.o_city || '',
        address_1: response.addresses?.origin?.o_address || '',
        address_gtm: response.addresses?.origin?.o_gtm_address || '',
        address_sailthru: response.addresses?.origin?.o_address_sailthru || '',
        zip: response.addresses?.origin?.o_zip || '',
      },
      destination: {
        country_code: response.addresses?.destination?.d_country || '',
        country_name: response.addresses?.destination?.d_country_name || '',
        state: response.addresses?.destination?.d_state || '',
        city: response.addresses?.destination?.d_city || '',
        address_1: response.addresses?.destination?.d_address || '',
        address_gtm: response.addresses?.destination?.d_gtm_address || '',
        address_sailthru: response.addresses?.destination?.d_address_sailthru || '',
        zip: response.addresses?.destination?.d_zip || '',
      },
      country_padding_days: response.country_padding_days || 0,
      messages: response.messages || {},
      drop_off: window.gon?.current_brand === 'shipcamps',
      combine: true,
    };
  };

  /**
   * Get pricing information for cruise booking
   * @param {Object} formData - Complete form data from CruiseBookingForm
   * @returns {Promise} - Promise resolving to pricing data
   */
  
  
  const getPricing = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      // Transform form data to API parameters
      const pricingParams = transformFormDataToPricingParams(formData);
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
        // Add any additional headers your API requires
        ...(window.gon?.csrf_token && { 'X-CSRF-Token': window.gon.csrf_token })
      };

      // Make API call with the new payload structure
      const response = await fetch('/api/v5/pricing', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          pricing: pricingParams
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform response to payload format
      const payload = transformResponseToPayload(data);
      
      setPricingData(payload);

      return {
        success: true,
        data: payload,
        raw_response: data
      };

    } catch (err) {
      const errorMessage = err.message || 'Failed to get pricing information';
      setError(errorMessage);
      
      return {
        success: false,
        message: errorMessage,
        data: null
      };
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPricing = async (pricingParams) => {
    setLoading(true);
    setError(null);

    try {
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
        // Add any additional headers your API requires
        ...(window.gon?.csrf_token && { 'X-CSRF-Token': window.gon.csrf_token })
      };

      // Make API call with the new payload structure
      const response = await fetch('/api/v5/pricing', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          pricing: pricingParams
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform response to payload format
      const payload = transformResponseToPayload(data);
      
      setPricingData(payload);

      return {
        success: true,
        data: payload,
        raw_response: data
      };

    } catch (err) {
      const errorMessage = err.message || 'Failed to get pricing information';
      setError(errorMessage);
      
      return {
        success: false,
        message: errorMessage,
        data: null
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear pricing data and reset state
   */
  const clearPricing = () => {
    setPricingData(null);
    setError(null);
  };

  return {
    loading,
    error,
    pricingData,
    getPricing,
    getDefaultPricing,
    clearPricing,
    // Utility functions for external use
    transformFormDataToPricingParams,
    transformResponseToPayload
  };
};